import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import { Authorization } from "../../../Context/Authorization";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../Context/AuthContext";
import { logActivity } from "../../../utils/LogActivity";
import Preloader from "../../../shared/PreLoaders";
import TableComponent from "../../../utils/TableComponent";

const inputStyle = {
  padding: "9px 12px", border: "1.5px solid #E2E8F0",
  borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff",
  outline: "none", transition: "border-color 0.2s", cursor: "pointer",
};

const ViewETVisitors = () => {
  const Api = process.env.REACT_APP_API;
  const navigate = useNavigate();
  const [visitors, setVisitors] = useState([]);
  const [ecoTourisms, setEcoTourisms] = useState([]);
  const [selectedEco, setSelectedEco] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const { userId } = useContext(AuthContext);
  const [revenuePopup, setRevenuePopup] = useState({
    open: false,
    details: [],
  });

  useEffect(() => {
    fetchEcoTourisms();
    fetchVisitors();
  }, []);

  useEffect(() => {
    fetchVisitors(selectedEco);
  }, [selectedEco]);

  const fetchEcoTourisms = async () => {
    try {
      const config = Authorization();
      const res = await axios.get(`${Api}/api/eco-tourism`, config);
      setEcoTourisms(res.data.data || []);
    } catch {
      toast.error("Failed to load Eco Tourism list");
    }
  };

  const fetchVisitors = async (ecoTourismId = "") => {
    try {
      setLoading(true);
      const config = Authorization();
      const url = ecoTourismId
        ? `${Api}/api/eco-tourism-table?ecoTourismId=${ecoTourismId}`
        : `${Api}/api/eco-tourism-table`;
      const res = await axios.get(url, config);
      setVisitors(res.data.data || []);
      await logActivity(userId, "Viewed ET Visitors", "Viewed ET Visitors Page", Api);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load visitor data");
    } finally {
      setLoading(false);
    }
  };

  const exportMonthlyReport = async () => {
    if (!selectedMonth) {
      toast.warn("Select month");
      return;
    }
    try {
      const config = Authorization();
      const res = await axios.get(
        `${Api}/api/eco-tourism-table/monthly?month=${selectedMonth}`,
        config
      );
      const data = res.data.data;
      if (!data.length) {
        toast.warn("No data for selected month");
        return;
      }

      const sheetData = [
        ["Sl.No", "Month", "Division", "ET site", "No of visitors (Nos)", "", "", "Total Revenue (in RS)", "Total no of EDC workers Engaged"],
        ["", "", "", "", "Adults", "Children", "Total", "", ""],
      ];
      data.forEach((row, index) => {
        sheetData.push([
          index + 1, row.month, row.divisionName, row.ecoTourismName,
          row.adults, row.children, row.totalVisitors, row.totalRevenue, row.totalWorkers,
        ]);
      });

      const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
      worksheet["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } },
        { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } },
        { s: { r: 0, c: 2 }, e: { r: 1, c: 2 } },
        { s: { r: 0, c: 3 }, e: { r: 1, c: 3 } },
        { s: { r: 0, c: 7 }, e: { r: 1, c: 7 } },
        { s: { r: 0, c: 8 }, e: { r: 1, c: 8 } },
        { s: { r: 0, c: 4 }, e: { r: 0, c: 6 } },
      ];
      worksheet["!cols"] = [
        { wch: 6 }, { wch: 10 }, { wch: 20 }, { wch: 25 },
        { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 18 }, { wch: 30 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Monthly ET Report");
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      saveAs(new Blob([excelBuffer]), `ET_Visitor_Report_${selectedMonth}.xlsx`);
    } catch (error) {
      toast.error("Failed to export report");
    }
  };

  const handleDeleteVisitor = async (id) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;

    try {
      const Config = Authorization();

      await axios.delete(`${Api}/api/eco-tourism-table/${id}`, Config); // ✅ FIXED

      setVisitors(visitors.filter((v) => v._id !== id));

      toast.success("Deleted Successfully");
    } catch (err) {
      console.error("Error deleting record", err);
      toast.error("Failed to delete record");
    }
  };

  const thStyle = { padding: "10px 12px", fontSize: 12, fontWeight: 700, color: "#2D3748", background: "#F7FAFC", border: "1px solid #E2E8F0", textAlign: "left" };
  const tdStyle = { padding: "9px 12px", fontSize: 12, color: "#4A5568", border: "1px solid #E2E8F0" };
  const popupTh = {
    padding: "10px",
    background: "#F7FAFC",
    borderBottom: "1px solid #E2E8F0",
    textAlign: "left",
  };

  const popupTd = {
    padding: "10px",
    borderBottom: "1px solid #EDF2F7",
  };
  const columns = [
    {
      headerName: "#",
      valueGetter: (params) => params.node.rowIndex + 1,
      width: 80,
      sortable: false,
      filter: false,
    },
    {
      headerName: "Date",
      field: "date",
      valueGetter: (params) =>
        new Date(params.data.date).toLocaleDateString("en-IN"),
    },
    {
      headerName: "Eco Tourism",
      valueGetter: (params) =>
        `${params.data.ecoTourismId?.ecoTourismName || ""} - ${params.data.ecoTourismId?.ecoTourismLocation || ""
        }`,
      minWidth: 250,
    },
    {
      headerName: "Visitors",
      valueGetter: (params) => {
        const summary = params.data.visitorSummary || {};

        return `Adults: ${summary.adults || 0} | Children: ${summary.children || 0} | Total: ${summary.totalVisitors || 0}`;
      },
      minWidth: 280,
    },
    {
      headerName: "Revenue Details",
      minWidth: 300,
      cellRenderer: (params) => {
        const details = params.data.visitorDetails || [];

        if (!details.length) {
          return (
            <span
              style={{
                color: "#A0AEC0",
                fontStyle: "italic",
                fontSize: 12,
              }}
            >
              No Fee Details
            </span>
          );
        }

        const firstItem = details[0];
        const remainingCount = details.length - 1;

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              width: "100%",
              padding: "2px 0",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                minWidth: 0,
                flex: 1,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: "#2E7D32",
                  flexShrink: 0,
                }}
              />

              <div
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: 12,
                  color: "#2D3748",
                }}
              >
                <strong>{firstItem.feeType}</strong>
                {firstItem.description && (
                  <span style={{ color: "#718096" }}>
                    {" "}({firstItem.description})
                  </span>
                )}
              </div>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#2D3748",
                }}
              >
                ₹{firstItem.total}
              </span>

              {remainingCount > 0 && (
                <button
                  onClick={() =>
                    setRevenuePopup({
                      open: true,
                      details,
                    })
                  }
                  style={{
                    background: "#E8F5E9",
                    color: "#2E7D32",
                    border: "1px solid #C8E6C9",
                    borderRadius: 20,
                    padding: "3px 10px",
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  +{remainingCount} more
                </button>
              )}
            </div>
          </div>
        );
      },
    },
    {
      headerName: "Total Amount",
      field: "totalAmount",
      valueGetter: (params) => `₹${params.data.totalAmount || 0}`,
    },
    {
      headerName: "EDC Workers",
      field: "edcWorkersEngaged",
    },
    {
      headerName: "Rooms Occupied",
      field: "roomsOccupied",
    },
    {
      headerName: "Actions",
      field: "action",
      sortable: false,
      filter: false,
      cellRenderer: (params) => (
        <div style={{ display: "flex", gap: 10 }}>
          <i
            className="mdi mdi-pen"
            style={{
              fontSize: "1.4rem",
              cursor: "pointer",
              color: "#2E7D32",
            }}
            onClick={() =>
              navigate(`/et-visitors/view/edit/${params.data._id}`)
            }
          />

          <i
            className="mdi mdi-delete"
            style={{
              fontSize: "1.4rem",
              cursor: "pointer",
              color: "#E53E3E",
            }}
            onClick={() => handleDeleteVisitor(params.data._id)}
          />
        </div>
      ),
      minWidth: 150,
    },
  ];


  return (
    <div>
      {loading && <Preloader loading={loading} />}

      {/* ── Page Header ── */}
      <div style={{ marginBottom: 20 }}>
        <h4 style={{ margin: 0, fontWeight: 700, color: "#1A202C", fontSize: 18 }}>ET Visitors</h4>
        <div style={{ fontSize: 12, color: "#718096", marginTop: 3 }}>
          <span style={{ color: "#A0AEC0" }}>Eco Tourism</span>
          <span style={{ margin: "0 6px", color: "#CBD5E0" }}>›</span>
          <span style={{ color: "#2E7D32", fontWeight: 600 }}>View ET Visitors</span>
        </div>
      </div>

      {/* ── Filter + Export Toolbar ── */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", padding: "16px 20px", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <span style={{ width: 30, height: 30, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14 }}>
            <i className="mdi mdi-filter-variant" />
          </span>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>Filter & Export</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <select value={selectedEco} onChange={(e) => setSelectedEco(e.target.value)}
            style={{ ...inputStyle, minWidth: 240 }}
            onFocus={(e) => (e.target.style.borderColor = "#4CAF50")}
            onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}>
            <option value="">All Eco Tourism</option>
            {ecoTourisms.map((eco) => (
              <option key={eco._id} value={eco._id}>
                {eco.ecoTourismName} – {eco.ecoTourismLocation}
              </option>
            ))}
          </select>

          <input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}
            style={{ ...inputStyle, minWidth: 180 }}
            onFocus={(e) => (e.target.style.borderColor = "#4CAF50")}
            onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")} />

          <button type="button" onClick={exportMonthlyReport}
            style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 18px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 3px 10px rgba(46,125,50,0.3)", whiteSpace: "nowrap" }}>
            <i className="mdi mdi-microsoft-excel" /> Export to Excel
          </button>
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: 16, padding: "10px 14px", background: "#FFF5F5", border: "1px solid #FED7D7", borderRadius: 8, fontSize: 12, color: "#E53E3E", fontWeight: 600 }}>
          {error}
        </div>
      )}

      {/* ── Table Card ── */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
          <span style={{ width: 30, height: 30, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15 }}>
            <i className="mdi mdi-account-group" />
          </span>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#fff", flex: 1 }}>Visitor Entries</span>
          <span style={{ padding: "3px 10px", background: "rgba(255,255,255,0.2)", borderRadius: 20, fontSize: 11, color: "#fff", fontWeight: 600 }}>
            {visitors.length} Records
          </span>
        </div>

        <div style={{ padding: 16 }}>
          <TableComponent
            data={visitors}
            columns={columns}
          />
        </div>
        {revenuePopup.open && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.35)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() =>
              setRevenuePopup({
                open: false,
                details: [],
              })
            }
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "700px",
                maxHeight: "80vh",
                overflowY: "auto",
                background: "#fff",
                borderRadius: 12,
                boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
                padding: 20,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 16,
                }}
              >
                <h4 style={{ margin: 0 }}>Revenue Breakdown</h4>

                <button
                  onClick={() =>
                    setRevenuePopup({
                      open: false,
                      details: [],
                    })
                  }
                  style={{
                    border: "none",
                    background: "transparent",
                    fontSize: 22,
                    cursor: "pointer",
                  }}
                >
                  ×
                </button>
              </div>

              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                }}
              >
                <thead>
                  <tr>
                    <th style={popupTh}>Fee Type</th>
                    <th style={popupTh}>Description</th>
                    <th style={popupTh}>Amount</th>
                  </tr>
                </thead>

                <tbody>
                  {revenuePopup.details.map((item, index) => (
                    <tr key={index}>
                      <td style={popupTd}>{item.feeType}</td>
                      <td style={popupTd}>{item.description || "-"}</td>
                      <td style={{ ...popupTd, textAlign: "right", fontWeight: 600 }}>
                        ₹{item.total}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewETVisitors;
